import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import { logos } from "@/logosData";

const ROLES = [
  "director",
  "technologist",
  "developer",
  "compositor",
  "cinematographer",
  "designer",
  "composer",
  "editor",
];

const HEAD_ASSETS = [
  { id: "favicon-ico", rel: "icon", href: "/favicon.ico", sizes: "any" },
  { id: "favicon-svg", rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
  { id: "favicon-32", rel: "icon", href: "/favicon-32x32.png", type: "image/png", sizes: "32x32" },
  { id: "favicon-16", rel: "icon", href: "/favicon-16x16.png", type: "image/png", sizes: "16x16" },
  { id: "apple-touch-icon", rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
  { id: "site-manifest", rel: "manifest", href: "/site.webmanifest" },
];

// Loads below-the-fold autoplay video only when it nears the viewport
const LazyVideo = ({ src, label }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (visible) ref.current.play()?.catch(() => {});
  }, [visible]);
  return (
    <video ref={ref} className="work-video" autoPlay loop muted playsInline preload="metadata" src={visible ? src : undefined} aria-label={label} />
  );
};

// Add new campaigns here: Instagram covers are fetched automatically; optional `cover` overrides with a local image
const CAMPAIGNS = [
  { id: "ajwavy", title: "AJ Wavy / North America Tour Campaign", url: "https://www.instagram.com/reel/DKGWdWoBGO3/", tag: "@itsajwavy", tagUrl: "https://www.instagram.com/itsajwavy/" },
  { id: "odyssey", title: "The Odyssey x Pardesi BTS / 2026 Event Launch", url: "https://www.instagram.com/reel/Dch1c6tKdn9/" },
  { id: "finale", title: "The Finale / 2025 Campaign", url: "https://www.instagram.com/reel/DQ8UconiuSb/" },
  { id: "knot", title: "Knot a Wedding / Campaign", url: "https://www.instagram.com/reel/DN9i1hgAYp8/" },
  { id: "vancouver", title: "Pardesi Vancouver Debut Launch", url: "https://www.instagram.com/reels/DMB9RPSJCl4/" },
];

const CampaignCard = ({ id, title, url, tag, tagUrl, cover: customCover }) => {
  const [failed, setFailed] = useState(false);
  const cover = customCover || `${process.env.REACT_APP_BACKEND_URL}/api/ig-cover?url=${encodeURIComponent(url)}`;
  return (
    <article className="campaign-card" data-testid={`campaign-${id}`}>
      <a className="campaign-cover" href={url} target="_blank" rel="noopener noreferrer" aria-label={`Watch ${title} on Instagram`} data-testid={`campaign-${id}-cover`}>
        {!failed && <img src={cover} alt={`${title}, campaign by Kartikye Kashyap`} loading="lazy" decoding="async" onError={() => setFailed(true)} />}
        {failed && <span className="campaign-cover-fallback">Instagram</span>}
        <span className="play-badge" aria-hidden="true">
          <svg viewBox="0 0 68 68" fill="none">
            <circle cx="34" cy="34" r="32.5" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
            <path d="M28 23 L47 34 L28 45 Z" fill="#fff" />
          </svg>
        </span>
      </a>
      <a className="campaign-title" href={url} target="_blank" rel="noopener noreferrer" data-testid={`campaign-${id}-title`}>{title}</a>
      <a className="campaign-tag" href={tagUrl || url} target="_blank" rel="noopener noreferrer" data-testid={`campaign-${id}-tag`}>{tag || "Instagram"} ↗</a>
    </article>
  );
};

const FOOTER_LINKS = [
  ["/ai-filmmaker.html", "AI Filmmaker"],
  ["/ai-commercial-director.html", "AI Ads"],
  ["/ai-artist.html", "AI Artist"],
  ["/creative-technologist.html", "Gaussian Splatting"],
  ["/work.html", "All Work"],
  ["/faq.html", "FAQ"],
  ["/es.html", "ES"],
  ["/fr.html", "FR"],
  ["/hi.html", "HI"],
];

function App() {
  const [activeSection, setActiveSection] = useState('intro');
  const [roleIdx, setRoleIdx] = useState(0);

  useEffect(() => {
    HEAD_ASSETS.forEach(({ id, ...attributes }) => {
      let link = document.head.querySelector(`[data-portfolio-head="${id}"]`);
      if (!link) {
        link = document.createElement('link');
        link.dataset.portfolioHead = id;
        document.head.appendChild(link);
      }
      Object.entries(attributes).forEach(([key, value]) => link.setAttribute(key, value));
    });
  }, []);

  // Cycle skill/role words one at a time
  useEffect(() => {
    const interval = setInterval(() => {
      setRoleIdx((prev) => (prev + 1) % ROLES.length);
    }, 900);
    return () => clearInterval(interval);
  }, []);

  const ytThumbFallback = (e) => {
    const el = e.currentTarget;
    if (!el.dataset.fallback) {
      el.dataset.fallback = '1';
      el.src = el.dataset.hq;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['intro', 'work', 'vita', 'contact'];
      const windowHeight = window.innerHeight;

      for (let section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <BrowserRouter>
      <div className="App" data-theme="dark">
        {/* Navigation */}
        <nav className="glass-nav" data-testid="main-navigation">
          <button
            onClick={() => scrollToSection('intro')}
            className={`nav-link ${activeSection === 'intro' ? 'active' : ''}`}
            data-testid="nav-intro"
          >
            INTRO
          </button>
          <button
            onClick={() => scrollToSection('work')}
            className={`nav-link ${activeSection === 'work' ? 'active' : ''}`}
            data-testid="nav-work"
          >
            WORK
          </button>
          <button
            onClick={() => scrollToSection('vita')}
            className={`nav-link ${activeSection === 'vita' ? 'active' : ''}`}
            data-testid="nav-vita"
          >
            VITA
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
            data-testid="nav-contact"
          >
            CONTACT
          </button>
        </nav>

        {/* INTRO Section */}
        <section id="intro" className="section intro-section" data-testid="intro-section">
          <div className="intro-layout">
            {/* Left: Text Content */}
            <div className="intro-text-content">
              <div className="vertical-line"></div>
              <div className="intro-text-inner">
                <h1 className="hero-title" data-testid="hero-title">
                  <span className="text-intro">i am</span>{' '}
                  <span className="name-primary">kartikye</span>{' '}
                  <span className="name-secondary">kashyap</span>
                </h1>

                <div className="statements" data-testid="intro-statements">
                  <p className="statement fade-in-1">
                    award winning multi-disciplinary multi-media director and artist.
                  </p>
                  <p className="statement fade-in-2">
                    i bridge emerging tech and creativity.
                  </p>
                  <p className="statement fade-in-3">
                    i think the tools we build shape the art we make.
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Looping Intro Video */}
            <div className="intro-video" data-testid="intro-video">
              <div className="intro-video-main">
                <video autoPlay loop muted playsInline preload="auto" poster="/videos/intro-poster.jpg" src="/videos/intro.mp4" aria-label="Showreel of AI filmmaking, motion capture and generative AI work by Kartikye Kashyap" />
              </div>
            </div>
          </div>

          {/* Logo Marquee - side-scrolling monotone logos */}
          <div className="logo-marquee" data-testid="logo-marquee" aria-label="Featured brands and collaborations">
            <div className="logo-marquee-track">
              {[...logos, ...logos, ...logos, ...logos, ...logos, ...logos].map((logo, index) => (
                <div className="logo-marquee-item" key={`logo-${index}`}>
                  <img src={logo.src} alt={logo.alt} draggable="false" />
                </div>
              ))}
            </div>
          </div>

          {/* Cycling role / skill word */}
          <div className="role-rotator" data-testid="role-rotator" aria-label="Roles and skills">
            <span className="role-bracket" aria-hidden="true">[</span>
            <span key={roleIdx} className="role-word" data-testid="role-word">
              {ROLES[roleIdx]}
            </span>
            <span className="role-bracket" aria-hidden="true">]</span>
          </div>
        </section>

        {/* WORK Section */}
        <section id="work" className="section" data-testid="work-section">
          <div className="content-wrapper">
            <div className="vertical-line"></div>
            <div className="main-content">
              <h2 className="section-title" data-testid="work-title">work</h2>

              {/* ---- ADS ---- */}
              <div className="work-subsection" data-testid="work-ads">
                <h3 className="work-subsection-title" data-testid="ads-subsection-title">Ads</h3>
                <div className="work-list">
                  {/* Just Rinse and Repeat */}
                  <article className="work-row" data-testid="ad-just-rinse">
                    <div className="work-row-media">
                      <a
                        className="media-frame media-link"
                        href="https://youtu.be/LZgk9qBBGcE?si=5YKzsxDYWLSBim8Q"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="ad-just-rinse-media"
                        aria-label="Play Just Rinse and Repeat on YouTube"
                      >
                        <img
                          className="work-thumb"
                          src="https://img.youtube.com/vi/LZgk9qBBGcE/maxresdefault.jpg"
                          data-hq="https://img.youtube.com/vi/LZgk9qBBGcE/hqdefault.jpg"
                          onError={ytThumbFallback}
                          alt="Just Rinse and Repeat, AI commercial directed by Kartikye Kashyap"
                          loading="lazy"
                        />
                        <span className="play-badge" aria-hidden="true">
                          <svg viewBox="0 0 68 68" fill="none">
                            <circle cx="34" cy="34" r="32.5" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
                            <path d="M28 23 L47 34 L28 45 Z" fill="#fff" />
                          </svg>
                        </span>
                      </a>
                    </div>
                    <div className="work-row-meta">
                      <span className="work-eyebrow">Ad</span>
                      <a
                        className="work-row-title work-link"
                        href="https://youtu.be/LZgk9qBBGcE?si=5YKzsxDYWLSBim8Q"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="ad-just-rinse-link"
                      >
                        Just Rinse and Repeat
                      </a>
                      <p className="work-row-role">Creative Direction, R&amp;D, Sound Design, Score &amp; Editing</p>
                      <span className="work-link-cue">Watch on YouTube →</span>
                    </div>
                  </article>

                  {/* Vanderbilt / Plastic Odyssey */}
                  <article className="work-row" data-testid="ad-vanderbilt">
                    <div className="work-row-media">
                      <div className="media-frame">
                        <video
                          className="work-video"
                          controls
                          playsInline
                          preload="metadata"
                          poster="/videos/vanderbilt-poster.jpg"
                          src="/videos/vanderbilt-plastic-odyssey.mp4"
                        />
                      </div>
                    </div>
                    <div className="work-row-meta">
                      <span className="work-eyebrow">Ad / Film</span>
                      <h4 className="work-row-title">Vanderbilt University Research Exhibition Voyage | Plastic Odyssey</h4>
                      <p className="work-row-role">Creative Direction &amp; Editing</p>
                    </div>
                  </article>

                  {/* Eureka Waterproof Notepad */}
                  <article className="work-row" data-testid="ad-eureka">
                    <div className="work-row-media">
                      <a
                        className="media-frame media-link"
                        href="https://youtu.be/sNxA2tPabgI?si=iUFI4kVle47oeRvi"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="ad-eureka-media"
                        aria-label="Play Eureka Waterproof Notepad on YouTube"
                      >
                        <img
                          className="work-thumb"
                          src="https://img.youtube.com/vi/sNxA2tPabgI/maxresdefault.jpg"
                          data-hq="https://img.youtube.com/vi/sNxA2tPabgI/hqdefault.jpg"
                          onError={ytThumbFallback}
                          alt="Eureka Waterproof Notepad, AI product ad directed by Kartikye Kashyap"
                          loading="lazy"
                        />
                        <span className="play-badge" aria-hidden="true">
                          <svg viewBox="0 0 68 68" fill="none">
                            <circle cx="34" cy="34" r="32.5" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
                            <path d="M28 23 L47 34 L28 45 Z" fill="#fff" />
                          </svg>
                        </span>
                      </a>
                    </div>
                    <div className="work-row-meta">
                      <span className="work-eyebrow">Ad</span>
                      <a
                        className="work-row-title work-link"
                        href="https://youtu.be/sNxA2tPabgI?si=iUFI4kVle47oeRvi"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="ad-eureka-link"
                      >
                        Eureka Waterproof Notepad
                      </a>
                      <p className="work-row-role">Creative Direction, R&amp;D &amp; Editing</p>
                      <span className="work-link-cue">Watch on YouTube →</span>
                    </div>
                  </article>
                </div>
              </div>

              {/* ---- FILMS ---- */}
              <div className="work-subsection" data-testid="work-films">
                <h3 className="work-subsection-title" data-testid="films-subsection-title">Films</h3>
                <div className="work-list">
                  <article className="work-row film-row" data-testid="film-infinity-row">
                    <div className="work-row-media">
                      <a
                        className="media-frame media-link"
                        href="https://runwayml.com/gen48?film=infinity"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="film-infinity"
                        aria-label="View ∞ on Runway"
                      >
                        <img
                          className="work-thumb"
                          src="https://customer-assets.emergentagent.com/job_f89c7579-45ce-4797-86a4-864b9c1026f7/artifacts/m8evkh55_EgxieYPDJCs.webp"
                          alt="∞, AI film by Kartikye Kashyap, Runway GEN:48 Aleph Edition People's Choice Winner"
                          loading="lazy"
                          decoding="async"
                        />
                        <span className="play-badge" aria-hidden="true">
                          <svg viewBox="0 0 68 68" fill="none">
                            <circle cx="34" cy="34" r="32.5" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
                            <path d="M28 23 L47 34 L28 45 Z" fill="#fff" />
                          </svg>
                        </span>
                      </a>
                    </div>
                    <div className="work-row-meta">
                      <span className="work-eyebrow">Film / Award</span>
                      <h4 className="work-row-title">∞ &nbsp;·&nbsp; People's Choice Winner</h4>
                      <p className="work-row-role">Direction, Concept &amp; Editing / Runway GEN:48 Aleph Edition</p>
                      <p className="work-row-stats">3000+ Submissions &nbsp;·&nbsp; 32 Finalists &nbsp;·&nbsp; 48-Hour AI Filmmaking Challenge</p>
                      <p className="work-row-desc">
                        Created using Runway's video models in 48 hours. I wanted to show a cohesive narrative while reiterating the cyclical themes of the film.
                      </p>
                      <span className="work-link-cue">View on Runway →</span>
                    </div>
                  </article>
                </div>
              </div>

              {/* ---- CAMPAIGNS ---- */}
              <div className="work-subsection work-subsection-major" data-testid="work-campaigns">
                <div className="campaign-heading-row" data-testid="campaign-heading-row">
                  <h2 className="section-title subsection-major-title" data-testid="campaigns-title">campaigns</h2>
                  <div className="campaign-brand">
                    <img className="campaign-logo" src="/logos/pardesi.png" alt="Pardesi" loading="lazy" decoding="async" data-testid="pardesi-logo" />
                  </div>
                </div>

                <div className="campaign-grid" data-testid="campaign-grid">
                  {CAMPAIGNS.map((c) => (
                    <CampaignCard key={c.id} {...c} />
                  ))}
                </div>
              </div>

              {/* ---- CREATIVE EXPERIMENTS ---- */}
              <div className="work-subsection work-subsection-major" data-testid="work-experiments">
                <h2 className="section-title subsection-major-title" data-testid="creative-experiments-title">creative experiments</h2>
                <div className="work-list experiment-list">
                  <article className="work-row experiment-row" data-testid="experiment-3d">
                    <div className="work-row-meta experiment-row-meta">
                      <p className="experiments-intro">
                        My experimentation guides my workflow, which utilises raw performance capture alongside emerging 3D and generative technology. Personal R&amp;D at the frontier of volumetric capture. Experimenting with <span className="hl">Gaussian Splatting</span> and <span className="hl">NeRFs</span> since 2022. Recently leveraged Apple&apos;s SHARP model to generate <span className="hl">4D Gaussian Splats</span> from a single monocular video, producing a sequential <span className="hl">.ply</span> outputs. Traditionally needing tens to hundreds of synchronised cameras which is the industry standard, which is also hugely compute-heavy. My workflow runs locally on consumer-level hardware.
                      </p>
                    </div>
                    <div className="work-row-media">
                      <div className="media-frame">
                        <LazyVideo src="/videos/experiments-3d.mp4" label="4D Gaussian Splatting and volumetric capture experiments by Kartikye Kashyap" />
                      </div>
                    </div>
                  </article>

                  <article className="work-row experiment-row" data-testid="experiment-genai">
                    <div className="work-row-meta">
                      <h4 className="work-row-title">GenAI Experiments</h4>
                      <p className="work-row-desc">A combined reel of using models from 2025 for previz-concept work.</p>
                    </div>
                    <div className="work-row-media">
                      <div className="media-frame">
                        <LazyVideo src="/videos/genai-experiments.mp4" label="Generative AI previz and concept experiments by Kartikye Kashyap" />
                      </div>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VITA Section */}
        <section id="vita" className="section section-alt" data-testid="vita-section">
          <div className="content-wrapper">
            <div className="vertical-line"></div>
            <div className="main-content">
              <h2 className="section-title" data-testid="vita-title">vita</h2>

              <div className="glass-card" data-testid="vita-content">
                {/* About */}
                <div className="vita-item">
                  <h3 className="vita-heading">About</h3>
                  <p className="vita-text">
                    My background is in Computer Science, but my passion is in the edit. I specialize in a production-first approach combining traditional videography, VFX, and sound design with GenAI workflows to push the boundaries of creative direction.
                  </p>
                </div>

                {/* Recognition */}
                <div className="vita-item">
                  <h3 className="vita-heading">Recognition</h3>
                  <p className="vita-text highlight-achievement">
                    People's Choice Winner - Runway GEN:48 Aleph Edition
                  </p>
                  <p className="vita-text">
                    Recognized for pushing creative boundaries with AI-generated video art. Selected from 3000+ submissions, among 32 finalists.
                  </p>
                </div>

                {/* Experience */}
                <div className="vita-item">
                  <h3 className="vita-heading">Experience</h3>

                  <div className="experience-entry">
                    <div className="experience-header">
                      <span className="experience-role">Co-founder</span>
                      <span className="experience-company">Pardesi</span>
                    </div>
                    <div className="experience-meta">
                      <span>2023 - Present</span>
                      <span className="meta-divider">·</span>
                      <span>Victoria, BC</span>
                    </div>
                    <p className="vita-text">
                      Co-founded and leading a cultural events organization. Led AJ Wavy Canada tour organization, managed event logistics and marketing, leveraged AI tools for content creation.
                    </p>
                  </div>

                  <div className="experience-entry">
                    <div className="experience-header">
                      <span className="experience-role">Technical and Creative Lead</span>
                      <span className="experience-company">Jambo Jar Technologies</span>
                    </div>
                    <div className="experience-meta">
                      <span>March 2024 - March 2025</span>
                      <span className="meta-divider">·</span>
                      <span>New Delhi</span>
                    </div>
                    <p className="vita-text">
                      Leading technical and creative teams in delivering AI-driven solutions. Overseeing technical and creative teams, managing AI/ML project pipelines, developing innovative tech solutions.
                    </p>
                  </div>

                  <div className="experience-entry">
                    <div className="experience-header">
                      <span className="experience-role">AI Engineer Intern</span>
                      <span className="experience-company">Denave</span>
                    </div>
                    <div className="experience-meta">
                      <span>April 2023 - September 2023</span>
                      <span className="meta-divider">·</span>
                      <span>New Delhi</span>
                    </div>
                    <p className="vita-text">
                      Led the development of an AI-driven database management system. Deployed in-house AI query system using LLMs, improved query response time and enhanced database efficiency.
                    </p>
                  </div>

                  <div className="experience-entry">
                    <div className="experience-header">
                      <span className="experience-role">AI Intern</span>
                      <span className="experience-company">Aftershoot (IIT Delhi)</span>
                    </div>
                    <div className="experience-meta">
                      <span>May 2022 - August 2022</span>
                      <span className="meta-divider">·</span>
                      <span>New Delhi</span>
                    </div>
                    <p className="vita-text">
                      Worked on AI photo-culling systems and model optimization. Tested and improved ML/AI models, enhanced model accuracy, collaborated with research teams.
                    </p>
                  </div>
                </div>

                {/* Philosophy */}
                <div className="vita-item">
                  <h3 className="vita-heading">Philosophy</h3>
                  <p className="vita-text">
                    I believe in creating tools that empower creativity rather than replace it.
                    Every project is an opportunity to push boundaries and challenge conventions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT Section */}
        <section id="contact" className="section section-alt" data-testid="contact-section">
          <div className="content-wrapper">
            <div className="vertical-line"></div>
            <div className="main-content">
              <h2 className="section-title" data-testid="contact-title">contact</h2>

              <div className="glass-card" data-testid="contact-content">
                <p className="contact-intro">
                  Let's create something exceptional together.
                </p>

                <div className="contact-links">
                  <a
                    href="mailto:kartikyekashyap@gmail.com"
                    className="contact-link"
                    data-testid="contact-email"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m2 7 10 7 10-7" />
                    </svg>
                    <span>Email</span>
                  </a>

                  <a
                    href="https://www.linkedin.com/in/kartikye-kashyap-35930527b/"
                    className="contact-link"
                    data-testid="contact-linkedin"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                      <rect x="2" y="9" width="4" height="12" />
                      <circle cx="4" cy="4" r="2" />
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <nav className="footer-links" aria-label="Explore" data-testid="footer-links">
            {FOOTER_LINKS.map(([href, label]) => (
              <a key={href} href={href} data-testid={`footer-link-${href.replace(/\W+/g, '-').replace(/^-|-html$/g, '')}`}>{label}</a>
            ))}
          </nav>
          <p className="footer-text">© 2025 Kartikye Kashyap. Creating without limits.</p>
        </footer>
      </div>
    </BrowserRouter>
  );
}

export default App;
